import React, { useState, useEffect } from "react";
import BotonManualHeader from "../../components/BotonManualHeader";
import {
  Container,
  Card,
  Table,
  Button,
  Badge,
  Row,
  Col,
  Form,
  OverlayTrigger,
  Tooltip,
  Spinner,
  ProgressBar,
  Alert,
} from "react-bootstrap";
import {
  collection,
  getDocs,
  getFirestore,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import {
  FaEye,
  FaEdit,
  FaUsers,
  FaBook,
  FaPlus,
  FaSync,
  FaSearch,
  FaCopy,
  FaFileExcel,
  FaGlobeAmericas,
  FaGraduationCap,
  FaChartPie,
  FaLayerGroup,
} from "react-icons/fa";
import * as XLSX from "xlsx";
import firebaseApp from "../../firebase/credenciales";
import { logBusinessAction } from "../../services/AuditService";

// Imagenes generadas
import bgCapacitaciones from "./images/bg_capacitaciones.png";
import cursoCover from "./images/curso_cover.png";

const db = getFirestore(firebaseApp);

const AdministradorCursosPage = ({ usuario }) => {
  const [cursos, setCursos] = useState([]);
  const [progresos, setProgresos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filtroCategoria, setFiltroCategoria] = useState("TODAS");
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (usuario?.ruc) {
      cargarDatos();
    }
  }, [usuario]);
  
  const cargarDatos = async () => {
    try {
      setLoading(true);
      const rucEmpresa = usuario?.ruc || "";
      const cursosRef = collection(db, `in_capacitacion/${rucEmpresa}/programas`);
      const cursosSnapshot = await getDocs(cursosRef);
      const cursosData = [];
  
      cursosSnapshot.forEach((doc) => {
        const data = doc.data();
        cursosData.push({
          id: doc.id,
          titulo: data.nombre || "Sin título",
          categoria: data.categoria || "General",
          descripcion: data.descripcion || "",
          modulos: data.modulos || [],
          duracionHoras: data.duracionHoras || 1,
          fecha_creacion: data.fechaCreacion,
        });
      });
  
      const progresosRef = collection(db, `in_capacitacion/${rucEmpresa}/progresos`);
      const progresosSnapshot = await getDocs(progresosRef);
      const progresosData = [];
      progresosSnapshot.forEach((doc) => {
        const data = doc.data();
        progresosData.push({
          dni: doc.id,
          modulosCompletados: Array.isArray(data.modulosCompletados)
            ? data.modulosCompletados
            : [],
        });
      });

      const docsCapRef = collection(
        db,
        `in_capacitacion/${rucEmpresa}/documentos_capacitacion`
      );
      const docsCapSnapshot = await getDocs(docsCapRef);
      const docsCapData = [];
      docsCapSnapshot.forEach((doc) => {
        docsCapData.push(doc.data() || {});
      });
  
      cursosData.forEach((curso) => {
        const usuariosInscritos = new Set();
        const usuariosCompletados = new Set();
        progresosData.forEach((progreso) => {
          if (!Array.isArray(progreso.modulosCompletados)) return;
          const modulosDelCurso = progreso.modulosCompletados.filter(m => m.startsWith(`${curso.id}_`));
          if (modulosDelCurso.length > 0) {
            usuariosInscritos.add(progreso.dni);
            if (modulosDelCurso.length === curso.modulos.length) usuariosCompletados.add(progreso.dni);
          }
        });

        // Señales adicionales desde documentos de capacitación
        docsCapData.forEach((d) => {
          if (d?.cursoId !== curso.id || !d?.dni || d?.visibleParaAdmin === false) return;
          usuariosInscritos.add(String(d.dni));
          if (d.tipoDocumento === "certificado" && d.url) {
            usuariosCompletados.add(String(d.dni));
          }
        });

        curso.total_inscritos = usuariosInscritos.size;
        curso.completados = usuariosCompletados.size;
        curso.engagement = curso.total_inscritos > 0 ? (curso.completados / curso.total_inscritos) * 100 : 0;
      });
  
      setCursos(cursosData.sort((a, b) => (b.fecha_creacion?.seconds || 0) - (a.fecha_creacion?.seconds || 0)));
      setProgresos(progresosData);
    } catch (error) {
      console.error("Error al cargar datos:", error);
    } finally {
      setLoading(false);
    }
  };

  const copiarID = (id) => {
    navigator.clipboard.writeText(id);
    setSuccess("ID de curso copiado al portapapeles");
    setTimeout(() => setSuccess(""), 3000);
  };

  const duplicarCurso = async (curso) => {
    if (!window.confirm(`¿Duplicar el curso "${curso.titulo}"?`)) return;
    try {
      setLoading(true);
      const rucEmpresa = usuario?.ruc || "";
      const nuevoCurso = {
        nombre: `${curso.titulo} (Copia)`,
        descripcion: curso.descripcion,
        asignadoA: ["TODOS"],
        activo: true,
        tipoRegistroSunafil: "CAPACITACION",
        horasSunafil: Number(curso.horasSunafil ?? curso.duracionHoras) || 1,
        capacitadorSunafil:
          curso.capacitadorSunafil || "OSCAR CRISTH RODRIGUEZ ARCE",
        cargoCapacitador: curso.cargoCapacitador || "CEO",
        organizador: curso.organizador || "OSCAR CRISTH RODRIGUEZ ARCE",
        sedeAdministrativa:
          curso.sedeAdministrativa ||
          "OTR.MZ RE LOTE 05 MZA. RE LOTE. 05 C.H. LA ESTANCIA DE LURIN",
        temaSunafil: curso.temaSunafil || curso.titulo,
        codigoFormatoSunafil: curso.codigoFormatoSunafil || "DIRIS-SST-F-006",
        versionFormatoSunafil: curso.versionFormatoSunafil || "01",
        fechaFormatoSunafil: curso.fechaFormatoSunafil || "28/02/23",
        modulos: (curso.modulos || []).map((modulo, index) => ({
          titulo: modulo.titulo || "",
          descripcion: modulo.descripcion || "",
          videoURL: modulo.videoURL || "",
          pdfURL: modulo.pdfURL || "",
          duracionMinutos: Number(modulo.duracionMinutos) || 10,
          obligatorio: modulo.obligatorio !== false,
          orden: Number(modulo.orden) || index + 1,
          preguntas: (modulo.preguntas || []).map((pregunta) => ({
            pregunta: pregunta.pregunta || "",
            opciones: Array.isArray(pregunta.opciones) ? pregunta.opciones : [],
            correcta: Number(pregunta.correcta) || 0,
          })),
        })),
        categoria: curso.categoria,
        duracionHoras: curso.duracionHoras,
        fechaCreacion: serverTimestamp(),
        fechaActualizacion: serverTimestamp(),
      };
      await addDoc(collection(db, `in_capacitacion/${rucEmpresa}/programas`), nuevoCurso);
      
      // AUDITORÍA
      try {
        await logBusinessAction("DUPLICACION_CURSO", "GestionCapacitaciones", {
          cursoOriginal: curso.titulo,
          cursoDuplicado: nuevoCurso.nombre,
          categoria: curso.categoria
        }, usuario);
      } catch (e) {
        console.warn("No se pudo registrar la auditoría de duplicación", e);
      }

      cargarDatos();
      setSuccess("Curso duplicado exitosamente");
    } catch (error) {
      console.error("Error al duplicar:", error);
      setError("Error al duplicar curso");
    } finally {
      setLoading(false);
    }
  };

  const exportarCursosExcel = () => {
    const datosExcel = cursosFiltrados.map((c, index) => ({
      index: index + 1,
      id: c.id,
      titulo: c.titulo,
      categoria: c.categoria,
      inscritos: c.total_inscritos,
      completados: c.completados,
    }));
    const ws = XLSX.utils.json_to_sheet(datosExcel);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Cursos");
    XLSX.writeFile(wb, `Plan_Cursos_${usuario?.ruc}.xlsx`);
  };

  const cursosFiltrados = cursos.filter((c) => {
    const matchBusqueda = c.titulo.toLowerCase().includes(searchTerm.toLowerCase()) || c.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCat = filtroCategoria === "TODAS" || c.categoria === filtroCategoria;
    return matchBusqueda && matchCat;
  });

  const uniqueCategorias = ["TODAS", ...new Set(cursos.map(c => c.categoria))];

  return (
    <div className="min-vh-100" style={{ 
      backgroundImage: `url(${bgCapacitaciones})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundAttachment: "fixed",
      padding: "20px 0"
    }}>
      <Container fluid style={{ width: "95%", maxWidth: "100%" }}>
        {loading && (
          <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-75" style={{ zIndex: 1080 }}>
            <div className="text-center">
              <Spinner animation="border" variant="light" style={{ width: "3rem", height: "3rem" }} />
              <p className="text-white mt-3">Sincronizando cat&aacute;logo...</p>
            </div>
          </div>
        )}

        <Card className="shadow-lg border-0" style={{ borderRadius: "15px", overflow: "hidden", minHeight: "90vh" }}>
          {/* Header standard Blue-Purple */}
          <Card.Header className="text-white py-4 position-relative" style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
            <div className="position-absolute end-0 top-50 translate-middle-y me-4" style={{ zIndex: 10 }}>
              <BotonManualHeader usuario={usuario} />
            </div>
            <div className="text-center">
              <h2 className="fw-bold mb-1"><FaBook className="me-3" />Administraci&oacute;n de Cursos</h2>
              <p className="mb-0 opacity-75">Crea, edita y gestiona el cat&aacute;logo maestro de capacitaciones</p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {success && <Alert variant="success" dismissible onClose={() => setSuccess("")}>{success}</Alert>}
            {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}

            {/* Top Action Card - Match Style */}
            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
              <Card.Body className="p-4">
                <Row className="align-items-center">
                  <Col md={1}><div className="rounded-3 p-3 text-center" style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "white" }}><FaLayerGroup size={24} /></div></Col>
                  <Col md={4}>
                    <h5 className="mb-1 fw-semibold">Gesti&oacute;n de Contenidos</h5>
                    <p className="text-muted mb-0 small">{cursos.length} programas registrados en total</p>
                  </Col>
                  <Col md={7} className="text-end">
                    <div className="d-flex gap-2 justify-content-end flex-wrap">
                      <Button variant="outline-primary" onClick={exportarCursosExcel} className="d-flex align-items-center gap-2" style={{ borderRadius: "10px" }}><FaFileExcel /> Reporte Excel</Button>
                      <Button className="d-flex align-items-center gap-2 text-white" onClick={() => navigate("/capacitaciones/crear-curso")} style={{ borderRadius: "10px", background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", border: "none" }}><FaPlus /> Nuevo Curso</Button>
                      <Button variant="outline-secondary" onClick={cargarDatos} style={{ borderRadius: "10px" }}><FaSync /></Button>
                      <Button variant="outline-primary" onClick={() => navigate("/capacitaciones/dashboard")} style={{ borderRadius: "10px" }}>Dashboard</Button>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* KPI Cards Strip */}
            <Row className="mb-4 g-3">
              <Col md={3}>
                <Card className="border-0 shadow-sm text-white h-100" style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", borderRadius: "15px" }}>
                  <Card.Body className="text-center">
                    <FaBook size={28} className="mb-2" />
                    <h3 className="fw-bold mb-0">{cursos.length}</h3>
                    <p className="mb-0 small fw-bold opacity-75">Programas Totales</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 shadow-sm text-white h-100" style={{ background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)", borderRadius: "15px" }}>
                  <Card.Body className="text-center">
                    <FaUsers size={28} className="mb-2" />
                    <h3 className="fw-bold mb-0">{progresos.length}</h3>
                    <p className="mb-0 small fw-bold opacity-75">Usuarios Participantes</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 shadow-sm text-white h-100" style={{ background: "linear-gradient(135deg, #17a2b8 0%, #007bff 100%)", borderRadius: "15px" }}>
                  <Card.Body className="text-center">
                    <FaChartPie size={28} className="mb-2" />
                    <h3 className="fw-bold mb-0">{(cursos.reduce((sum, c) => sum + (c.engagement || 0), 0) / (cursos.length || 1)).toFixed(1)}%</h3>
                    <p className="mb-0 small fw-bold opacity-75">Engagement Promedio</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 shadow-sm text-white h-100" style={{ background: "linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)", borderRadius: "15px" }}>
                  <Card.Body className="text-center">
                    <FaGraduationCap size={28} className="mb-2" />
                    <h3 className="fw-bold mb-0">{cursos.filter(c => c.categoria !== "General").length}</h3>
                    <p className="mb-0 small fw-bold opacity-75">Cursos de la Empresa</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Filters Row */}
            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
              <Card.Body className="p-3">
                <Row className="g-2">
                  <Col md={8}>
                    <div className="position-relative">
                      <FaSearch className="position-absolute" style={{ left: "12px", top: "50%", transform: "translateY(-50%)", color: "#6c757d" }} />
                      <Form.Control type="text" placeholder="Buscar por t&iacute;tulo o ID maestro..." className="ps-5" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ borderRadius: "10px" }} />
                    </div>
                  </Col>
                  <Col md={4}>
                    <Form.Select value={filtroCategoria} onChange={(e) => setFiltroCategoria(e.target.value)} style={{ borderRadius: "10px" }}>
                      {uniqueCategorias.map(cat => <option key={cat} value={cat}>{cat === "TODAS" ? "Todas las Categor&iacute;as" : cat}</option>)}
                    </Form.Select>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Courses Table - Match Table Style */}
            <div className="table-responsive">
              <Table hover className="align-middle shadow-sm bg-white" style={{ borderRadius: "15px", overflow: "hidden" }}>
                <thead className="bg-light text-secondary small text-uppercase fw-bold">
                  <tr>
                    <th className="ps-4">Programa / Curso</th>
                    <th className="text-center">Categor&iacute;a</th>
                    <th className="text-center">Inscritos</th>
                    <th className="text-center">Engagement</th>
                    <th className="text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {cursosFiltrados.length === 0 ? (
                    <tr><td colSpan="5" className="text-center py-4 text-muted">No se encontraron cursos con los criterios de b&uacute;squeda</td></tr>
                  ) : cursosFiltrados.map((curso) => (
                    <tr key={curso.id}>
                      <td className="ps-4 py-3">
                        <div className="d-flex align-items-center">
                          <img src={cursoCover} alt="Cover" className="rounded-3 me-3" style={{ width: "45px", height: "45px", objectFit: "cover", border: "1px solid #eee" }} />
                          <div>
                            <div className="fw-bold text-dark">{curso.titulo}</div>
                            <div className="d-flex align-items-center">
                              <code className="text-primary small me-2">{curso.id}</code>
                              <OverlayTrigger overlay={<Tooltip>Copiar ID curso</Tooltip>}>
                                <Button variant="link" className="p-0 text-muted" onClick={() => copiarID(curso.id)}><FaCopy size={12} /></Button>
                              </OverlayTrigger>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="text-center">
                        <Badge bg={curso.categoria === "General" ? "info" : "secondary"}>{curso.categoria}</Badge>
                      </td>
                      <td className="text-center fw-bold">{curso.total_inscritos}</td>
                      <td style={{ width: "15%" }}>
                        <div className="px-2">
                          <ProgressBar now={curso.engagement || 0} variant="primary" style={{ height: "6px" }} />
                          <small className="text-muted d-block text-center mt-1">{curso.engagement?.toFixed(0) || 0}%</small>
                        </div>
                      </td>
                      <td className="text-center">
                        <div className="d-flex justify-content-center gap-1">
                          <OverlayTrigger overlay={<Tooltip>Ver curso</Tooltip>}>
                            <Button variant="link" size="sm" onClick={() => navigate(`/capacitaciones/curso/${curso.id}`)}><FaEye /></Button>
                          </OverlayTrigger>
                          <OverlayTrigger overlay={<Tooltip>Editar contenido</Tooltip>}>
                            <Button variant="link" size="sm" onClick={() => navigate(`/capacitaciones/editar-curso/${curso.id}`)}><FaEdit /></Button>
                          </OverlayTrigger>
                          <OverlayTrigger overlay={<Tooltip>Duplicar curso</Tooltip>}>
                            <Button variant="link" size="sm" className="text-success" onClick={() => duplicarCurso(curso)}><FaCopy /></Button>
                          </OverlayTrigger>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default AdministradorCursosPage;
